<mat-card class="rounded-3xl border border-slate-200 p-4 shadow-lg shadow-slate-200/60">
  <h3 class="mb-3 text-lg font-semibold text-slate-900">Provider referral</h3>

  @if (interventionStore.referralsLoading()) {
    <div class="flex items-center gap-3 text-sm text-slate-600">
      <mat-spinner diameter="20" />
      Loading referral status...
    </div>
  } @else if (!currentReferral()) {
    @if (needsReferralConsent()) {
      <p class="mb-3 rounded-2xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
        Referral consent must be recorded on the Consent tab before a referral can be created.
      </p>
    }
    @if (canCreateReferral()) {
      @if (referralModel() === 'SELECT') {
        <div class="flex flex-wrap items-end gap-3">
          <searchable-lookup-select
            class="min-w-64"
            [formControl]="selectAffiliateControl"
            label="Affiliate"
            placeholder="Select affiliate"
            searchPlaceholder="Search affiliates"
            loadingLabel="Loading affiliates..."
            [options]="eligibleOptions()"
            [loading]="affiliateStore.eligibleAffiliatesLoading()" />
          <button
            mat-flat-button
            type="button"
            [disabled]="!selectAffiliateControl.value || interventionStore.creatingReferral()"
            (click)="createReferral()">
            @if (interventionStore.creatingReferral()) {
              <mat-spinner diameter="20" class="mr-2" />
            }
            Create referral
          </button>
        </div>
      } @else if (referralModel() === 'EXPRESS') {
        <button
          mat-flat-button
          type="button"
          [disabled]="interventionStore.creatingReferral()"
          (click)="createReferral()">
          @if (interventionStore.creatingReferral()) {
            <mat-spinner diameter="20" class="mr-2" />
          }
          Create referral (broadcast to eligible affiliates)
        </button>
      } @else {
        <p class="text-sm text-slate-500">This intervention type has no referral model configured.</p>
      }
    } @else if (isStaff() && !canCreateInCurrentStage()) {
      <p class="text-sm text-slate-500">
        No referral was created for this intervention. A referral can only be created while it is in the
        Assessment or Referral stage (currently {{ currentStageLabel() }}).
        @if (isBeforeAssessment()) {
          Move the intervention on to Assessment first (status on the Details tab), or complete its assessment.
        }
      </p>
    } @else {
      <p class="text-sm text-slate-500">No referral has been created for this intervention yet.</p>
    }
  } @else {
    <div class="mb-4 text-sm text-slate-700">
      <p><span class="font-semibold">Type:</span> {{ currentReferral()!.referralType }}</p>
      <p><span class="font-semibold">Status:</span> {{ currentReferral()!.acceptanceStatus ?? 'Pending' }}</p>
    </div>

    @if (currentReferral()!.affiliates.length > 0) {
      <div class="mb-4">
        <p class="mb-1 text-sm font-semibold text-slate-900">Pool</p>
        @for (item of currentReferral()!.affiliates; track item.id) {
          <div class="flex items-center justify-between border-b border-slate-100 py-1 text-sm text-slate-700">
            <span>{{ item.affiliateName }} {{ item.affiliateSurname }}</span>
            <span class="text-xs" [class.text-slate-400]="!item.isActive">{{ poolStatusLabel(item) }}</span>
          </div>
        }
      </div>
    }

    @if (currentReferral()!.assignments.length > 0) {
      <div class="mb-4">
        <p class="mb-1 text-sm font-semibold text-slate-900">Responses</p>
        @for (item of currentReferral()!.assignments; track item.id) {
          <div class="border-b border-slate-100 py-1 text-sm text-slate-700">
            <p>{{ item.assignmentStatus }}</p>
            @if (item.declineReason) {
              <p class="text-xs text-slate-500">Decline reason: {{ item.declineReason }}</p>
            }
            @if (item.withdrawnReason) {
              <p class="text-xs text-slate-500">Withdraw reason: {{ item.withdrawnReason }}</p>
            }
          </div>
        }
      </div>
    }

    @if (canReassign()) {
      <div class="flex flex-wrap items-end gap-3">
        <searchable-lookup-select
          class="min-w-64"
          [formControl]="reassignAffiliateControl"
          label="Reassign to"
          placeholder="Select affiliate"
          searchPlaceholder="Search affiliates"
          loadingLabel="Loading affiliates..."
          [options]="reassignOptions()"
          [loading]="affiliateStore.eligibleAffiliatesLoading()" />
        <button
          mat-flat-button
          type="button"
          [disabled]="!reassignAffiliateControl.value || interventionStore.respondingToReferral()"
          (click)="reassign()">
          Reassign
        </button>
      </div>
      @if (reassignOptions().length === 0 && !affiliateStore.eligibleAffiliatesLoading()) {
        <p class="mt-2 text-sm text-slate-500">
          Every eligible affiliate has already responded to this referral, so there is no one left to reassign it to.
        </p>
      }
    }

    @if (canAcceptOrDecline()) {
      <div class="mt-3 grid gap-3">
        <mat-form-field appearance="outline">
          <mat-label>Decline reason (required to decline, not to accept)</mat-label>
          <input matInput type="text" [formControl]="declineReasonControl">
        </mat-form-field>
        <div class="flex gap-3">
          <button
            mat-flat-button
            type="button"
            [disabled]="interventionStore.respondingToReferral()"
            (click)="accept()">
            Accept
          </button>
          <button
            mat-stroked-button
            type="button"
            [disabled]="!declineReasonControl.value.trim() || interventionStore.respondingToReferral()"
            (click)="decline()">
            Decline
          </button>
        </div>
      </div>
    }

    @if (canWithdraw()) {
      <div class="mt-3 grid gap-3">
        <mat-form-field appearance="outline">
          <mat-label>Withdraw reason</mat-label>
          <input matInput type="text" [formControl]="withdrawReasonControl">
        </mat-form-field>
        <div class="flex gap-3">
          <button
            mat-stroked-button
            type="button"
            [disabled]="!withdrawReasonControl.value.trim() || interventionStore.respondingToReferral()"
            (click)="withdraw()">
            Withdraw acceptance
          </button>
        </div>
      </div>
    }
  }
</mat-card>
